import { useEffect, useRef, useState } from "react";

import type { AdminTeacherActivity } from "@canvaschool/contracts";

export type ReachedStep =
  keyof AdminTeacherActivity["teachers"][number]["reachedAt"];
export type SeriesUnit = "day" | "week" | "month";
export type SeriesView = "cumulative" | "new";

export type SeriesBucket = { start: Date; label: string; value: number };

/** Most buckets one chart draws; older ones are dropped to keep the DOM small. */
export const MAX_BUCKETS = 366;

const pad = (n: number) => String(n).padStart(2, "0");

/** "YYYY-MM-DD" in local time, the format of `<input type="date">`. */
export const toDateInput = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const fromDateInput = (value: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return null;
  }
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

/** First instant of the day, Monday-based week or month that holds `date`. */
const bucketStart = (date: Date, unit: SeriesUnit) => {
  const y = date.getFullYear();
  const m = date.getMonth();
  const d = date.getDate();
  if (unit === "month") {
    return new Date(y, m, 1);
  }
  if (unit === "week") {
    return new Date(y, m, d - ((date.getDay() + 6) % 7));
  }
  return new Date(y, m, d);
};

const nextBucket = (start: Date, unit: SeriesUnit) => {
  const y = start.getFullYear();
  const m = start.getMonth();
  const d = start.getDate();
  if (unit === "month") {
    return new Date(y, m + 1, 1);
  }
  return new Date(y, m, d + (unit === "week" ? 7 : 1));
};

const bucketLabel = (start: Date, unit: SeriesUnit) => {
  if (unit === "month") {
    return `${start.getFullYear()}-${pad(start.getMonth() + 1)}`;
  }
  const text = `${start.getMonth() + 1}/${start.getDate()}`;
  return unit === "week" ? `${text}~` : text;
};

/**
 * Buckets of ACTIVE teachers by when they reached `step`. "cumulative" counts
 * those who had reached it by the end of each bucket, "new" those who reached
 * it inside the bucket. Teachers who have not reached the step now are never
 * counted, so the last cumulative bucket up to today is today's funnel count.
 */
export const stepSeries = (
  activity: AdminTeacherActivity,
  step: ReachedStep,
  unit: SeriesUnit,
  start: Date,
  end: Date,
  view: SeriesView,
): SeriesBucket[] => {
  const times = activity.teachers
    .filter((teacher) => teacher.status === "ACTIVE")
    .map((teacher) => teacher.reachedAt[step])
    .filter((value): value is string => value !== null)
    .map((value) => new Date(value).getTime());

  const first = bucketStart(start, unit);
  const last = bucketStart(end < start ? start : end, unit);
  const buckets: SeriesBucket[] = [];
  for (
    let cursor = first;
    cursor.getTime() <= last.getTime();
    cursor = nextBucket(cursor, unit)
  ) {
    const from = cursor.getTime();
    const to = nextBucket(cursor, unit).getTime();
    buckets.push({
      start: cursor,
      label: bucketLabel(cursor, unit),
      value: times.filter((time) =>
        view === "cumulative" ? time < to : time >= from && time < to,
      ).length,
    });
  }
  return buckets.slice(-MAX_BUCKETS);
};

/** Width used until the container is measured (and where nothing measures). */
const FALLBACK_WIDTH = 640;
const MARGIN = { top: 28, right: 8, bottom: 26, left: 32 };
const LABEL_WIDTH = 46;

/** Round the top of the axis up to a whole-number tick step: 1, 2, 5, 10, ... */
export const niceTicks = (max: number) => {
  const rough = Math.max(max, 1) / 4;
  const magnitude = Math.max(1, 10 ** Math.floor(Math.log10(rough)));
  const step =
    [1, 2, 5, 10].map((m) => m * magnitude).find((s) => s >= rough) ??
    10 * magnitude;
  const top = Math.max(1, Math.ceil(max / step)) * step;
  const ticks: number[] = [];
  for (let value = 0; value <= top; value += step) {
    ticks.push(value);
  }
  return { top, ticks };
};

/** Bar with only its top corners rounded, sitting on the baseline. */
const barPath = (x: number, y: number, w: number, h: number) => {
  const r = Math.min(4, w / 2, h);
  return `M${x} ${y + h}V${y + r}Q${x} ${y} ${x + r} ${y}H${x + w - r}Q${
    x + w
  } ${y} ${x + w} ${y + r}V${y + h}Z`;
};

/** Single-series bar chart as inline SVG, with a table for assistive tech. */
export const TeacherStepChart = ({
  buckets,
  stepLabel,
  view,
}: {
  buckets: SeriesBucket[];
  stepLabel: string;
  view: SeriesView;
}) => {
  const [active, setActive] = useState<number | null>(null);
  const [tableOpen, setTableOpen] = useState(false);
  // Drawn in real pixels so text keeps its size at any container width.
  const wrapRef = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState(0);
  useEffect(() => {
    const element = wrapRef.current;
    if (!element || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(() =>
      setMeasured(Math.floor(element.clientWidth)),
    );
    observer.observe(element);
    setMeasured(Math.floor(element.clientWidth));
    return () => observer.disconnect();
  }, []);
  const width = measured > 0 ? measured : FALLBACK_WIDTH;
  const height = width < 480 ? 180 : 220;
  const viewLabel = view === "cumulative" ? "누적" : "새로 도달";
  const max = Math.max(0, ...buckets.map((bucket) => bucket.value));
  const summary = `${stepLabel} ${viewLabel}: ${buckets[0]?.label ?? ""}부터 ${
    buckets[buckets.length - 1]?.label ?? ""
  }까지 ${buckets.length}칸, 가장 큰 값 ${max}명`;

  if (max === 0) {
    return (
      <div ref={wrapRef} className="canvaschool-step-chart">
        <p className="canvaschool-hint">
          이 기간에 {stepLabel}에 도달한 교사가 없습니다.
        </p>
      </div>
    );
  }

  const { top, ticks } = niceTicks(max);
  const plotW = width - MARGIN.left - MARGIN.right;
  const plotH = height - MARGIN.top - MARGIN.bottom;
  const slot = plotW / buckets.length;
  const barW = Math.max(1, Math.min(28, slot - 2));
  const every = Math.ceil(LABEL_WIDTH / slot);
  const y = (value: number) => MARGIN.top + plotH - (value / top) * plotH;
  const tip = active === null ? null : buckets[active] ?? null;
  const tipText = tip ? `${tip.label} · ${viewLabel} ${tip.value}명` : "";
  const tipW = tipText.length * 7.5 + 16;
  const tipX =
    tip && active !== null
      ? Math.min(
          Math.max(MARGIN.left + active * slot + slot / 2 - tipW / 2, 0),
          width - tipW,
        )
      : 0;

  return (
    <div ref={wrapRef} className="canvaschool-step-chart">
      <svg
        width={width}
        height={height}
        role="img"
        aria-label={summary}
        onPointerLeave={() => setActive(null)}
      >
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              x1={MARGIN.left}
              x2={width - MARGIN.right}
              y1={y(tick)}
              y2={y(tick)}
              stroke="var(--cs-line)"
              strokeWidth={tick === 0 ? 1 : 0.5}
              opacity={tick === 0 ? 1 : 0.6}
            />
            <text
              x={MARGIN.left - 6}
              y={y(tick) + 4}
              textAnchor="end"
              fontSize="11"
              fill="var(--cs-muted)"
            >
              {tick}
            </text>
          </g>
        ))}
        {buckets.map((bucket, index) => {
          const x = MARGIN.left + index * slot + (slot - barW) / 2;
          const barH = plotH - (y(bucket.value) - MARGIN.top);
          return (
            <g key={bucket.start.getTime()}>
              {/* Full-height hit area so thin and short bars are easy to hover. */}
              <rect
                x={MARGIN.left + index * slot}
                y={MARGIN.top}
                width={slot}
                height={plotH}
                fill="transparent"
                onPointerEnter={() => setActive(index)}
                onClick={() => setActive(index)}
              />
              {bucket.value > 0 && (
                <path
                  d={barPath(x, y(bucket.value), barW, barH)}
                  fill="var(--cs-warning)"
                  opacity={active === null || active === index ? 1 : 0.6}
                  pointerEvents="none"
                />
              )}
              {index % every === 0 && (
                <text
                  x={MARGIN.left + index * slot + slot / 2}
                  y={height - 8}
                  textAnchor="middle"
                  fontSize="11"
                  fill="var(--cs-muted)"
                >
                  {bucket.label}
                </text>
              )}
            </g>
          );
        })}
        {tip && (
          <g pointerEvents="none">
            <rect
              x={tipX}
              y={2}
              width={tipW}
              height={22}
              rx={4}
              fill="var(--cs-ink)"
            />
            <text
              x={tipX + tipW / 2}
              y={17}
              textAnchor="middle"
              fontSize="12"
              fill="var(--cs-on-ink)"
            >
              {tipText}
            </text>
          </g>
        )}
      </svg>
      <details onToggle={(event) => setTableOpen(event.currentTarget.open)}>
        <summary>표로 보기</summary>
        {/* Built only when opened so it stays out of the page's own table. */}
        {tableOpen && (
          <div className="canvaschool-table-wrap">
            <table className="canvaschool-table">
              <thead>
                <tr>
                  <th scope="col">기간 시작</th>
                  <th scope="col">
                    {stepLabel} ({viewLabel})
                  </th>
                </tr>
              </thead>
              <tbody>
                {buckets.map((bucket) => (
                  <tr key={bucket.start.getTime()}>
                    <td>{bucket.label}</td>
                    <td>{bucket.value}명</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </details>
    </div>
  );
};
